import React, { useState, useMemo, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TableSortLabel,
    Paper,
    TextField,
    InputAdornment,
    Box,
    Typography,
    CircularProgress
} from '@material-ui/core';
import { BiSearch } from 'react-icons/bi';
import { useTableStyles } from './styles';
import { PAGINATION_CONFIG, PARTICIPATION_THRESHOLDS } from '../../constants';

/**
 * Componente de tabla genérica reutilizable
 * Acepta configuración de columnas dinámicas
 * 
 * @param {Object} props - Props del componente
 * @param {Array} props.data - Array de objetos con datos
 * @param {Array} props.columns - Array de configuración de columnas
 * @param {boolean} props.loading - Estado de carga
 * @param {boolean} props.searchable - Habilitar búsqueda (default: true)
 * @param {Array} props.searchFields - Campos en los que buscar (default: todos)
 * @param {string} props.title - Título de la tabla
 * @param {string} props.emptyMessage - Mensaje cuando no hay datos
 * @param {Array} props.rowsPerPageOptions - Opciones de filas por página
 * @returns {JSX.Element} Tabla genérica con funcionalidades
 */
export default function GenericTable({
    data = [],
    columns = [],
    loading = false,
    searchable = true,
    searchFields = [],
    title = 'Tabla de Datos',
    emptyMessage = 'No se encontraron resultados',
    rowsPerPageOptions = PAGINATION_CONFIG.rowsPerPageOptions,
}) {
    const classes = useTableStyles();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(PAGINATION_CONFIG.defaultRowsPerPage);
    const [orderBy, setOrderBy] = useState(columns.length > 0 ? columns[0].id : '');
    const [order, setOrder] = useState('asc');
    const [searchTerm, setSearchTerm] = useState('');

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
        setPage(0);
    };

    useEffect(() => {
        setPage(0);
    }, [data]);

    const descendingComparator = useCallback((a, b, field) => {
        const aVal = a[field];
        const bVal = b[field];
        if (bVal < aVal) return -1;
        if (bVal > aVal) return 1;
        return 0;
    }, []);

    const getComparator = useCallback((ord, field) => {
        return (a, b) => {
            const aVal = a[field];
            const bVal = b[field];
            if (aVal == null && bVal == null) return 0;
            if (aVal == null) return 1;
            if (bVal == null) return -1;
            return ord === 'desc'
                ? descendingComparator(a, b, field)
                : -descendingComparator(a, b, field);
        };
    }, [descendingComparator]);

    const getParticipationClass = (participacion) => {
        if (participacion >= PARTICIPATION_THRESHOLDS.high) return classes.participationHigh;
        if (participacion >= PARTICIPATION_THRESHOLDS.medium) return classes.participationMedium;
        return classes.participationLow;
    };

    const fieldsToSearch = useMemo(() => {
        if (searchFields.length > 0) return searchFields;
        return columns.map(col => col.id);
    }, [searchFields, columns]);

    // Filtrar datos por búsqueda
    const filteredData = useMemo(() => {
        const searchLower = searchTerm.toLowerCase();
        return data.filter((row) => {
            if (!searchTerm || !searchable) return true;
            return fieldsToSearch.some(field =>
                (row[field] || '').toString().toLowerCase().includes(searchLower)
            );
        });
    }, [data, searchTerm, searchable, fieldsToSearch]);

    // Ordenar datos
    const sortedData = useMemo(() => {
        return [...filteredData].sort(getComparator(order, orderBy));
    }, [filteredData, order, orderBy, getComparator]);

    // Paginar datos
    const paginatedData = useMemo(() => {
        return sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    }, [sortedData, page, rowsPerPage]);

    // Renderizar valor de celda con formatter
    const renderCellValue = (column, row) => {
        const value = row[column.id];
        if (column.formatter && typeof column.formatter === 'function') {
            return column.formatter(value, row);
        }
        return value;
    };

    // Renderizar icono del encabezado
    const renderHeaderIcon = (column) => {
        if (!column.icon) return null;
        const IconComponent = column.icon;
        return <IconComponent className={classes.headerIcon} />;
    };

    if (columns.length === 0) {
        return (
            <Paper className={classes.tableContainer}>
                <Box p={2} className={classes.emptyState}>
                    <Typography variant="body1">
                        No hay columnas configuradas
                    </Typography>
                </Box>
            </Paper>
        );
    }

    return (
        <Paper className={classes.tableContainer}>
            <Box p={2}>
                {(searchable || title) && (
                    <Box className={classes.searchContainer}>
                        {title && (
                            <Typography variant="h6" className={classes.tableTitle}>
                                {title}
                            </Typography>
                        )}
                        {searchable && (
                            <TextField
                                className={classes.searchInput}
                                placeholder="Buscar..."
                                variant="outlined"
                                size="small"
                                value={searchTerm}
                                onChange={handleSearchChange}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <BiSearch size={18} />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        )}
                    </Box>
                )}

                <TableContainer>
                    <Table>
                        <TableHead className={classes.tableHeader}>
                            <TableRow>
                                {columns.map((column) => (
                                    <TableCell
                                        key={column.id}
                                        className={classes.tableHeaderCell}
                                        align={column.align || 'left'}
                                        style={{ width: column.width }}
                                    >
                                        {column.sortable ? (
                                            <TableSortLabel
                                                active={orderBy === column.id}
                                                direction={orderBy === column.id ? order : 'asc'}
                                                onClick={() => handleRequestSort(column.id)}
                                                className={classes.sortLabel}
                                            >
                                                {renderHeaderIcon(column)}
                                                {column.label}
                                            </TableSortLabel>
                                        ) : (
                                            <Box style={{ display: 'inline-flex', alignItems: 'center' }}>
                                                {renderHeaderIcon(column)}
                                                {column.label}
                                            </Box>
                                        )}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                <TableRow>
                                    <TableCell colSpan={columns.length} className={classes.emptyState}>
                                        <CircularProgress size={40} />
                                    </TableCell>
                                </TableRow>
                            ) : paginatedData.length > 0 ? (
                                paginatedData.map((row, rowIndex) => (
                                    <TableRow key={page * rowsPerPage + rowIndex} hover className={classes.tableRow}>
                                        {columns.map((column) => (
                                            <TableCell
                                                key={`${row.id || rowIndex}-${column.id}`}
                                                className={`${classes.tableCell} ${
                                                    column.id === 'participacion'
                                                        ? getParticipationClass(row[column.id])
                                                        : ''
                                                }`}
                                                align={column.align || 'left'}
                                            >
                                                {renderCellValue(column, row)}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={columns.length} className={classes.emptyState}>
                                        <Typography variant="body1">
                                            {emptyMessage}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    className={classes.tablePagination}
                    rowsPerPageOptions={rowsPerPageOptions}
                    component="div"
                    count={filteredData.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="Filas por página:"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                />
            </Box>
        </Paper>
    );
}

GenericTable.propTypes = {
    data: PropTypes.arrayOf(PropTypes.object),
    columns: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired,
            sortable: PropTypes.bool,
            align: PropTypes.oneOf(['left', 'center', 'right']),
            icon: PropTypes.elementType,
            width: PropTypes.string,
            formatter: PropTypes.func,
        })
    ),
    loading: PropTypes.bool,
    searchable: PropTypes.bool,
    searchFields: PropTypes.arrayOf(PropTypes.string),
    title: PropTypes.string,
    emptyMessage: PropTypes.string,
    rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
};
